import React, { useState, useEffect } from 'react';
import { Form, Input, Radio, Select, Collapse, Button, Space, Row, Col, Typography, message } from 'antd';
import { physicalExamSectionsFull } from '../utils/physicalExamParametersFull';
import PhysicalExamLanguageToggle from './PhysicalExamLanguageToggle';
import { usePhysicalExamLanguage } from '../hooks/usePhysicalExamLanguage';
import axios from 'axios';

const { Panel } = Collapse;
const { TextArea } = Input;
const { Title } = Typography;

const StructuredPhysicalExamFormFull = ({ initialData = {}, onSave, onCancel, patientId, encounterId, onSuccess }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Language management hook
  const {
    currentLanguage,
    changeLanguage,
    getTranslatedParameterName,
    getTranslatedOption,
    getTranslatedSectionTitle,
    getTranslatedText,
    isVietnamese
  } = usePhysicalExamLanguage();

  useEffect(() => {
    form.resetFields(); // Clear any existing values first

    if (initialData && initialData.findingsArray && initialData.findingsArray.length > 0) {
      // If there's initial data, populate the form with it
      const existingValues = {};
      initialData.findingsArray.forEach(finding => {
        // The form structure is { [param.key]: { value: '...', notes: '...' } }
        existingValues[finding.key] = {
          value: finding.value,
          notes: finding.notes,
        };
      });
      form.setFieldsValue(existingValues);
    } else {
      // Otherwise, set default "normal" values for a new exam
      const initialNormalValues = {};
      physicalExamSectionsFull.forEach(section => {
        section.parameters.forEach(param => {
          if (param.options && param.options.length > 0) {
            initialNormalValues[param.key] = { value: param.options[0] };
          }
        });
      });
      form.setFieldsValue(initialNormalValues);
    }
  }, [form, initialData]);

  const handleSetAllNormal = () => {
    const normalValues = {};
    physicalExamSectionsFull.forEach(section => {
      section.parameters.forEach(param => {
        if (param.options && param.options.length > 0) {
          normalValues[param.key] = { value: param.options[0], notes: '' };
        } else {
          normalValues[param.key] = { value: undefined, notes: '' };
        }
      });
    });
    form.setFieldsValue(normalValues);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Get all form values including optional notes fields
      const values = form.getFieldsValue();
      console.log('Raw Form Values (getFieldsValue):', values); // For debugging

      // Also validate required fields
      await form.validateFields();
      console.log('Form validation passed'); // For debugging
      console.log('Language Debug - isVietnamese:', isVietnamese, 'currentLanguage:', currentLanguage); // Debug language state

      // Generate findings array and HTML summary for PatientFlowsheet compatibility with language support
      const findingsArray = [];
      const comprehensiveExamTitle = isVietnamese ? 'Kết quả Khám Lâm Sàng Toàn Diện' : 'Comprehensive Physical Exam Findings';
      const notesOnlyText = isVietnamese ? 'Chỉ có ghi chú' : 'Notes only';

      console.log('HTML Summary Title:', comprehensiveExamTitle); // Debug title generation

      let htmlSummary = `<h5>${comprehensiveExamTitle}</h5>`;
      let currentSectionHtml = "";

      physicalExamSectionsFull.forEach(section => {
        const translatedSectionTitle = getTranslatedSectionTitle(section.title);
        currentSectionHtml = `<strong>${translatedSectionTitle}:</strong><ul style="list-style-type: none; padding-left: 10px; margin-top: 0;">`;
        let sectionHasFindings = false;

        section.parameters.forEach(param => {
          const finding = values[param.key];
          const findingValue = finding?.value;
          const findingNotes = finding?.notes?.trim();

          // Debug logging for notes
          if (finding || findingNotes) {
            console.log(`[DEBUG] Parameter ${param.key}:`, {
              finding,
              findingValue,
              findingNotes,
              hasNotes: !!findingNotes,
              notesLength: findingNotes?.length || 0
            });
          }

          if (findingValue || findingNotes) {
            sectionHasFindings = true;

            // Create finding object for findingsArray (using standardized parameter names)
            const findingObject = {
              key: param.key, // Standardized parameter key
              name: param.key, // Use standardized key as name for backend storage
              value: findingValue || 'N/A',
              notes: findingNotes || null,
              category: section.title,
              type: 'Physical Exam Detail'
            };
            findingsArray.push(findingObject);

            // Determine abnormality for HTML summary highlighting
            const isAbnormal = (findingValue && param.options && findingValue !== param.options[0]) || !!findingNotes;

            const translatedParameterName = getTranslatedParameterName(param.key);
            currentSectionHtml += `<li>${translatedParameterName}: `;
            if (isAbnormal) currentSectionHtml += `<strong style="color: orange;">`;

            const translatedValue = findingValue ? getTranslatedOption(findingValue) : notesOnlyText;
            currentSectionHtml += translatedValue;

            if (findingNotes) {
              console.log(`[DEBUG] Adding notes to HTML: "${findingNotes}" for parameter ${param.key}`);
              currentSectionHtml += ` (${findingNotes})`;
            }

            if (isAbnormal) currentSectionHtml += `</strong>`;
            currentSectionHtml += `</li>`;
          }
        });

        currentSectionHtml += "</ul>";
        if (sectionHasFindings) {
          htmlSummary += currentSectionHtml;
        }
      });

      console.log('Generated findingsArray:', findingsArray);
      console.log('Generated htmlSummary:', htmlSummary);
      console.log('[DEBUG] HTML Summary with notes check:', htmlSummary.includes('(') ? 'Contains notes' : 'No notes found');

      // Return data in the format expected by PatientFlowsheet
      const examData = { findingsArray, htmlSummary };

      // Signal success to parent with the structured data
      if (onSave) onSave(examData);
      if (onSuccess) onSuccess(htmlSummary);

    } catch (errorInfo) {
      console.log('Failed:', errorInfo);
      message.error('Please complete the required fields.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form form={form} layout="vertical" name="structured_physical_exam_full">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0 }}>
          {getTranslatedText('Comprehensive Physical Exam')}
        </Title>
        <Space>
          <Button onClick={handleSetAllNormal}>
            {getTranslatedText('Set All to Normal')}
          </Button>
        </Space>
      </div>

      <PhysicalExamLanguageToggle onLanguageChange={changeLanguage} />
      <Collapse accordion defaultActiveKey={['skin']}>
        {physicalExamSectionsFull.map(section => (
          <Panel header={getTranslatedSectionTitle(section.title)} key={section.key} forceRender>
            {section.parameters.map(param => {
              // Helper function to render the correct input type
              const renderInput = () => {
                switch (param.inputType) {
                  case 'radio':
                    return (
                      <Radio.Group optionType="button">
                        {param.options.map(option => (
                          <Radio key={option} value={option}>
                            {getTranslatedOption(option)}
                          </Radio>
                        ))}
                      </Radio.Group>
                    );
                  case 'select':
                    return (
                      <Select placeholder={isVietnamese ? `Chọn ${getTranslatedParameterName(param.key)}` : `Select ${param.label}`} allowClear>
                        {param.options.map(option => (
                          <Select.Option key={option} value={option}>
                            {getTranslatedOption(option)}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  // Add cases for 'numeric' or other types if needed later
                  default:
                    return <Input placeholder="Undefined input type" disabled />;
                }
              };

              return (
                // Use the parameter key as the base for the form item names
                <div key={param.key} style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                  <Row gutter={16} align="middle">
                    <Col xs={24} sm={10}> {/* Label column */}
                      <Typography.Text strong>
                        {getTranslatedParameterName(param.key)}
                      </Typography.Text>
                    </Col>
                    <Col xs={24} sm={14}> {/* Input controls column */}
                      <Row gutter={8}>
                        <Col xs={24} md={12}> {/* Structured Input */}
                          {/* Form Item for the structured value */}
                          <Form.Item
                            name={[param.key, 'value']} // Nested name: e.g., ['conjunctiva', 'value']
                            noStyle // Render without label/styling here
                          >
                            {renderInput()}
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}> {/* Optional Notes */}
                          {/* Form Item for the optional notes */}
                          <Form.Item
                            name={[param.key, 'notes']} // Nested name: e.g., ['conjunctiva', 'notes']
                            noStyle
                          >
                            <TextArea
                              rows={1}
                              placeholder={getTranslatedText('Optional Notes')}
                              maxLength={200} // Add a reasonable max length
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              );
            })}
          </Panel>
        ))}
      </Collapse>
      <Space style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel}>
          {getTranslatedText('Cancel')}
        </Button>
        <Button type="primary" onClick={handleSave} loading={isSubmitting}>
          {getTranslatedText('Save Exam Results')}
        </Button>
      </Space>
    </Form>
  );
};

export default StructuredPhysicalExamFormFull;
